import { applyVatIfNeeded } from '../constants/vat';
// 💷 THE DISPLAY ADOPTS THE STORAGE RULE — see THE SIGN AND THE ROUNDING RULE
// in `formatCurrency`'s docblock. `utils/money.js` is the byte-mirrored client
// copy of `server/helpers/money.js`, so this is the SAME rule the column holds.
import { roundMoney } from './money';

/**
 * Format a number as GBP currency with commas (e.g., £1,095,144.29).
 *
 * Treats the input as the literal amount to format (no VAT logic).
 *
 * ⚠️ This used to say "use this for raw Ex VAT input echoes" and to point at
 * `formatCurrencyExt` for "values that should honour the active production's VAT
 * mode". **There is no VAT mode any more** — it was removed from the UI on
 * 2026-08-08 (reason in `hooks/useVatMode.js`), and stored values can no longer
 * be described as Ex VAT, because each department now decides for itself whether
 * it types ex- or inc-VAT prices. Both functions in this file format the literal
 * number they are given; `formatCurrencyExt` is retained with its uplift
 * parameter but every live caller passes `includeVat = false`.
 *
 * @param {number|string} amount - The amount to format
 * @param {boolean} includeSymbol - Whether to include the £ symbol (default: true)
 * @returns {string} Formatted currency string
 */
export function formatCurrency(amount, includeSymbol = true) {
  // 🪤 **THIS `|| 0` DOES NOT DO WHAT IT LOOKS LIKE IT DOES.** It has twice been
  // written up — in a job-list entry and in a brief built from it — as the reason
  // `formatCurrency('')` renders `£0.00`. It is not. `Number('')`, `Number(null)`
  // and `Number(false)` are ALL already `0` without it; it changes exactly TWO
  // inputs, `undefined` and the literal `NaN`, turning both into `0`. So deleting
  // it does not fix the absence problem — it makes it WORSE, by exposing
  // `formatCurrency(undefined)` as the string `£NaN`. Measured 2026-09-20 by
  // breaking it both ways; both reds are pinned in `formatCurrency.test.js`.
  //
  // ⚖️ **RULED 2026-09-21 — THIS FUNCTION STAYS A DUMB CONVERTER.** Pete, shown
  // both options: *"Guard the absence marker AT THE CALL SITES. Leave
  // `formatCurrency` a dumb converter."* So the two behaviours below are now
  // DELIBERATE, not owed work: (1) an ABSENT amount renders `£0.00`, and (2) a
  // non-numeric amount renders the literal `£NaN`. **Neither is a bug HERE.**
  // Anything that must not show a fabricated figure guards its own value before
  // calling, because only the call site knows what its marker has to read like.
  //
  // 🔑 **THE ONE DEFINITION THE GUARDS USE — THREE STATES, NOT TWO.**
  //   • `null` / `undefined` / `''`      → ABSENT    → the site's marker
  //   • present but `Number()` non-finite → MALFORMED → left LOUD (`£NaN`)
  //   • any finite number, `0` INCLUDED   → the money → `£0.00`, `-£75.00`, …
  // 🚨 **A real, deliberate `0` is NOT an absence** — £0 is a legitimate value
  // elsewhere in this app (owner ruling), so every falsy test on a money value
  // is a defect. 🚨 **And absent and malformed must never share a marker**: a
  // £1,000 figure can reach the browser as the STRING `"1,000"`, and
  // `Number('1,000')` is `NaN` — marking that "absent" would report a real,
  // entered thousand pounds as an empty field and turn a visible defect silent.
  //
  // 🗺️ **WHERE THE GUARDS LIVE.** Grep the call SHAPE, never a line number —
  // one of the three citations this comment used to carry had already gone
  // stale (it named a guard in `pages/MarketplaceCreateListing.js`, a file
  // which by then had DELETED its `formatCurrency` import entirely, as its own
  // header records). Four marker styles, and the differences are deliberate:
  //   • **em dash `'—'`** for a standalone figure — a cell, a bar label, a
  //     money row. Grep `moneyOrAbsent(` and the `valuePrefix === '£'` branches
  //     in `BarChart.js` / `VerticalBarChart.js`. Chosen over a plain `'-'`
  //     because a hyphen beside money reads as a MINUS SIGN, and because
  //     `orderListPdf.js` already converts a `'-'` INTO its `EM_DASH`.
  //   • **the whole clause suppressed** where the amount is interpolated
  //     MID-SENTENCE — `'—'` there would read *"… (—)"* or *"— estimated —
  //     not yet added to the budget"*. Grep `budgetAmountSuffix(` and
  //     `utils/gagBudgetStatus.js`'s `estimatedCost != null` branches.
  //   • **`'-'`** — `GagDetailPanel`'s read-only Estimated cost field, which
  //     sits beside `estimated_days ?? '-'` and matches ITS NEIGHBOUR rather
  //     than this file's default. Deliberately not standardised.
  //   • **`null`** — `gagCsvExport.js`, where the marker must be an EMPTY CSV
  //     FIELD, not a dash a spreadsheet would import as text.
  // See `components/gags/CLAUDE.md` on `gagBudgetStatus` omitting the amount
  // "rather than fabricating £0.00" — the precedent the ruling followed.
  //
  // 🔢 **24 direct call sites in 13 production files, measured 2026-09-21**
  // (the 25th `formatCurrency(` in this tree is `formatCurrencyExt`'s own, just
  // below). **7 interpolate mid-sentence.** Most of the rest are provably
  // finite at source — a `reduce` over `parseFloat(x) || 0`, a `> 0` gate, a
  // `Number.isFinite` filter — and are deliberately NOT guarded.
  //
  // 🪤 **RE-MEASURE BEFORE QUOTING — AND RE-MEASURE *HOW*, BECAUSE THE OBVIOUS
  // METHOD IS THE INCOMPLETE ONE. A grep for `formatCurrency(` CANNOT SEE A
  // CALL PASSED BY REFERENCE.** `valueFormatter={formatCurrency}` on the three
  // dashboards is a real invocation — `PieChart` calls it as
  // `valueFormatter(slice.value)` — and it is absent from the 24 by
  // construction, not by oversight. Those four are safe (their data builder
  // does `Number(s.total_spend) || 0` before the chart sees anything), which is
  // why this is a note about the METHOD rather than a defect. Grep the
  // identifier without its paren to find the by-reference sites.
  //
  // 🔑 `formatCurrencyExt` below is NOT affected by any of this: `applyVatIfNeeded`
  // normalises with `Number.isFinite` before this function ever sees the value, so
  // the 229 `useVatMode().formatMoney` call sites are insulated. Proven in §5 of
  // the test file. The ruling cost 24 call sites, not 254 — and of those, four
  // already guarded and fifteen were proven finite at source, so it landed on
  // five expressions in four files.
  // ═════ 🚨 THE SIGN AND THE ROUNDING RULE (2026-09-22) ═════
  //
  // ⚖️ Owner: *"fix so -£19.33 is correct"*. The sign goes BEFORE the symbol —
  // UK convention — where it used to land inside it as `£-19.33`.
  //
  // 🚨 **AND THE ROUNDING RULE MOVED WITH IT, WHICH IS THE HALF THAT WAS
  // COSTING MONEY.** `toLocaleString` breaks a tie AWAY FROM ZERO, so a stored
  // `-1.335` DISPLAYED as `-£1.34` while the 2dp write boundary HEALED it to
  // `-1.33`. Renaming a line therefore changed its amount by a penny, away from
  // the figure the user was looking at — *"the backend changes the totals"*,
  // arriving through the change meant to prevent it.
  //
  // 🪤 **THE MECHANISM IS `Math.round`'s TIE RULE, NOT THE EPSILON NUDGE — do
  // not blame the wrong line.** `Math.round` breaks a tie toward **+∞**, so
  // `Math.round(-133.5)` is `-133`. That makes `roundMoney` ASYMMETRIC:
  // `r(x) !== -r(-x)` on a large fraction of half-penny magnitudes (a sweep of
  // 100,000 found 90,852). The epsilon is absorbed entirely above |4| and is
  // irrelevant here.
  //
  // ⚖️ **THE DISPLAY ADOPTS THE STORAGE RULE, NEVER THE REVERSE.** Storage is
  // the authority and `utils/money.js` is a byte-mirror of the server's rule;
  // making the store match `Intl` would FORK the money rule rather than unify
  // it. Pinned by `formatCurrency.test.js` §2c, including a swept grid rather
  // than a single example.
  //
  // 🪤 `roundMoney` answers `null` for anything it cannot round — a non-finite
  // value, or one whose `x100` step overflows — so the raw number is the
  // fallback. That is what keeps `£NaN` LOUD (the 2026-09-21 ruling) and stops
  // an enormous figure rendering as `£null`.
  //
  // 🪤 **NEGATIVE ZERO.** `roundMoney(-0.004)` is `-0`, and `-0 < 0` is FALSE,
  // so the sign is suppressed and a sub-penny debit renders `£0.00` rather than
  // `-£0.00` — money that would read as a debt. The plain `< 0` test is correct
  // ONLY because of that; do not "improve" it into an `Object.is` check.
  const num = Number(amount || 0);
  const rounded = roundMoney(num);
  const value = rounded === null ? num : rounded;
  const negative = value < 0;
  const formatted = Math.abs(value).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  const sign = negative ? '-' : '';
  // 🔑 `includeSymbol = false` is BYTE-IDENTICAL to what it always returned —
  // the sign was already outside the (absent) symbol there.
  return includeSymbol ? `${sign}£${formatted}` : `${sign}${formatted}`;
}

/**
 * Format a number as GBP currency, optionally uplifted by VAT and labelled.
 *
 * When `includeVat` is true, the amount is multiplied by (1 + VAT_RATE)
 * before formatting. When `showLabel` is true, " Inc VAT" / " Ex VAT" is
 * appended.
 *
 * Prefer the useVatMode() hook in components — it binds includeVat to the
 * active production automatically.
 *
 * @param {number|string} amount
 * @param {boolean} includeVat
 * @param {object} [opts]
 * @param {boolean} [opts.showLabel] - Append " Inc VAT" / " Ex VAT" suffix
 * @returns {string}
 */
export function formatCurrencyExt(amount, includeVat, { showLabel = false } = {}) {
  const formatted = formatCurrency(applyVatIfNeeded(amount, includeVat));
  return showLabel ? `${formatted} ${includeVat ? 'Inc VAT' : 'Ex VAT'}` : formatted;
}

/**
 * Format a number with commas and 2 decimal places (no currency symbol)
 *
 * 🔻 **MEASURED DEAD 2026-09-20 — IT HAS NO CALLERS.** Not one reference anywhere
 * in `client/src`, and `git grep formatNumber -- client/src` matches exactly one
 * file (this one) on EVERY local and remote ref, all five unmerged branches
 * included. Re-measure before quoting that; do not inherit it. Kept for now
 * because removing a public export is a separate decision, not because anything
 * uses it. 🪤 It carries the SAME `|| 0` and the same literal-`NaN` output as
 * `formatCurrency` above.
 *
 * ✅ **THAT SENTENCE ASKED FOR SOMETHING THE RULING DOES NOT REQUIRE, AND IT IS
 * ANSWERED RATHER THAN DELETED.** It read *"whatever the owner rules there must
 * be applied here too, or the survivor becomes a quiet counter-example to the
 * ruling."* The ruling of 2026-09-21 is **"guard at the CALL SITES"** — and
 * this function has no call sites, so there is nothing to guard and no
 * counter-example to be. Its `|| 0` is unchanged deliberately, exactly as
 * `formatCurrency`'s is. 🔑 **If a caller is ever added, the obligation is on
 * THAT caller**, to the same three-state definition written above.
 *
 * @param {number|string} value - The value to format
 * @returns {string} Formatted number string
 */
export function formatNumber(value) {
  return Number(value || 0).toLocaleString('en-GB', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}
